import * as React from "react";
import { useQuery } from "@apollo/client";
import { postAdapter } from "../../core/adapters/postAdapter";
import { PageCategory, Tag } from "../../core/types";
import { CategoryArticleList, Layout } from "../../components";
import { NoResult } from "./components/NoResult";
import { searchResultsQuery } from "./queries";
import { maybe } from "../../core/utils";
import useDebounce, { mapNodes } from "../../ssr/utils";
import { Box, Flex } from "@chakra-ui/layout";
import {
  Button,
  DrawerContent,
  Drawer,
  DrawerOverlay,
  Modal,
  ModalContent,
  Spinner,
  Divider,
  DrawerHeader,
} from "@chakra-ui/react";
import { RTag } from "@components/Texts/Tag/Tag";
import { generateBlogTagUrl } from "@core/paths";
import { Link as GatsbyLink } from 'gatsby';
import { WidgetTitle } from "@components/Texts/WidgetTitle/WidgetTitle";
import { Search } from "src/components/MainMenu/search";
import { useMemo, useState } from "react";

interface IProps {
  searchQuery?: string
  onSearch: any;
  isMobile: boolean;
}

export const SearchResultPage: React.FC<IProps> = ({
                                                     searchQuery,
                                                     onSearch,
                                                     isMobile
                                                   }): JSX.Element => {
  const [loadingMore, setLoadingMore] = useState(false)
  const debounceSearch = useDebounce(searchQuery, 500);

  const { loading, error, data, fetchMore } = useQuery(searchResultsQuery, {
    variables: { searchQuery: debounceSearch },
  });

  if (error) return <div>{`Error! ${error}`}</div>;

  const posts = maybe(() => data.posts.nodes, []);
  const tags = maybe(() => data.tags.nodes, []);
  const pageInfo = maybe(() => data.posts.pageInfo);
  const hasNextPage = maybe(() => pageInfo.hasNextPage);
  const hasPrevPage = maybe(() => pageInfo.hasPrevPage);
  const endCursor = maybe(() => pageInfo.endCursor);

  const searchContent = useMemo(() => (
    <Box p="0" overflowY="scroll" maxH="100%"
    css={{
      '&::-webkit-scrollbar': {
        width: '0',
      },
      '&::-webkit-scrollbar-track': {
        width: '0',
      },
      '&::-webkit-scrollbar-thumb': {
        display: 'none',
      },
    }}
    >
      <Box position="fixed" zIndex="1000" background="white" top="0" left="0" w="100%"  gridArea="search" alignItems="center" justifyContent="flex-start">
        <Search isResults={true} autoFocus={false} searchQuery={searchQuery} onSearch={onSearch} />
      </Box>
      <Box w="100%" mt="64px">
        {
          !posts.length && loading
            ? (
              <Flex justifyContent="center" alignItems="center">
                <Spinner />
              </Flex>
            )
            :  !posts.length
              ? <NoResult/>
              : (
                <Box p="1rem 15px 2rem">
                  <CategoryArticleList posts={posts} />
                  {
                    // todo fix load more
                    false && (
                    <Button
                      background="transparent !important"
                      color="#3C3C3C"
                      w="100%"
                      my="1rem"
                      fontSize="14px"
                      border="1px solid #E6EAF0"
                      borderRadius="4px"
                      boxShadow="0 0 4px #E6EAF0"
                      className="button"
                      onClick={async () => {
                        setLoadingMore(true)
                        await fetchMore({
                          variables: {
                            afterPost: endCursor,
                          },

                          updateQuery: (prev = {}, { fetchMoreResult }) => {
                            setLoadingMore(false)
                            if (!fetchMoreResult) return (prev);
                            return {
                              ...prev,
                              posts: {
                                ...fetchMoreResult.posts,
                                nodes: [
                                  ...prev.posts.nodes,
                                  ...fetchMoreResult.posts.nodes,
                                ],
                              },
                            };
                          },
                        });
                      }}
                    >
                      {!loadingMore ? 'Pokaż kolejne wpisy' : <Spinner />}
                    </Button>
                  )}
                </Box>
              )
        }
      </Box>
      {
        !!tags.length && (
          <Box p="0 15px 3rem">
            <WidgetTitle mb="1rem">Znalezione tagi: {tags.length}</WidgetTitle>
            {tags.map(tag => <RTag key={`${tag.id}_${tag.name}`} as={GatsbyLink} to={generateBlogTagUrl(tag.slug)} backgroundColor="#E7F2FF">{tag.name}</RTag>)}
          </Box>
        )
      }
    </Box>
  ), [posts, loading, tags, loadingMore, searchQuery, error, hasNextPage, endCursor])

  return (
    isMobile
      ? (
        <Modal size="full" isOpen={true} onClose={() =>{}}>
          <ModalContent>
            {searchContent}
          </ModalContent>
        </Modal>
      )
      : (
        <Drawer
          isOpen={true}
          placement='right'
          onClose={() => onSearch('')}
        >
          <DrawerOverlay />
          <DrawerContent>
            {searchContent}
          </DrawerContent>
        </Drawer>
      )
  );
};
